import { CopropertyDto } from 'api/coproperty';
import { Sizes } from 'enums/sizes';
import {
  CopropertySummaryPanel,
  MriCopropertySection
} from 'features/subscription-process/pricing-process/components/insured-entity-card/CopropertySummaryPanel';
import { usePanel } from 'hooks/usePanel';
import { AlertTriangle, Check } from 'lucide-react';
import { FC, useCallback, useMemo } from 'react';
import { useQuoteContractualizationData } from '../../store';
import { useRefetchQuoteData } from '../../hooks/useRefetchQuoteData';
import { ClaimsStatementUploader } from './mri/ClaimsStatementUploader';
import { useClaimsStatementDocument } from './mri/useClaimsStatementDocument';
import { EditCopropertyBuildingsPanel } from './mri/EditCopropertyBuildingsPanel';
import { EditCopropertyClaimsPanel } from './mri/EditCopropertyClaimsPanel';
import { EditCopropertyComplementsPanel } from './mri/EditCopropertyComplementsPanel';
import { EditCopropertySurveyPanel } from './mri/EditCopropertySurveyPanel';

/** Sections modifiables sans quitter la contractualisation. */
const EDITABLE_SECTIONS: readonly MriCopropertySection[] = ['survey', 'buildings', 'complements', 'claims'];

/** Bandeau d'alerte des pièces et engagements attendus, au format des messages du récapitulatif. */
const ClaimsNotice: FC<{ tone: 'warning' | 'success'; children: string }> = ({ tone, children }) => (
  <p
    className={`mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-[13px] ${
      tone === 'warning' ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'
    }`}
  >
    {tone === 'warning' ? (
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
    ) : (
      <Check className="mt-0.5 h-4 w-4 flex-none" />
    )}
    <span>{children}</span>
  </p>
);

/**
 * Contenu du panneau « risque » de la contractualisation MRI : le récapitulatif de la
 * copropriété, avec un lien « Modifier » par section qui ouvre son sous-panneau. Rien ne
 * renvoie au parcours : la modification se fait ici, le récapitulatif se relit ensuite
 * depuis les données de contractualisation.
 */
export const MriRiskPanel: FC = () => {
  const quoteData = useQuoteContractualizationData();
  const { openPanel } = usePanel();
  const refetchQuoteData = useRefetchQuoteData();

  const coproperty = (quoteData?.coproperty ?? null) as CopropertyDto | null;
  const riskId = quoteData?.riskId;
  const quoteId = quoteData?.quoteId;
  const canEditRisk = quoteData?.permissions?.canEditRisk === true;
  const claimsHistoryCompleted = quoteData?.copropertyClaimsHistoryCompleted === true;
  const { data: claimsStatement, reload: reloadClaimsStatement } = useClaimsStatementDocument(quoteId);
  const claimsStatementUploaded = claimsStatement?._exists === true;

  const handleClaimsStatementChanged = useCallback(async () => {
    await reloadClaimsStatement();
    await refetchQuoteData();
  }, [reloadClaimsStatement, refetchQuoteData]);

  const openSection = useCallback(
    (section: MriCopropertySection) => {
      if (!coproperty) return;

      switch (section) {
        case 'survey':
          if (!riskId) return;
          openPanel(
            { title: "Questionnaire d'appréciation du risque", size: Sizes['2xl'] },
            <EditCopropertySurveyPanel coproperty={coproperty} riskId={riskId} quoteId={quoteId} />
          );
          return;
        case 'buildings':
          openPanel(
            { title: 'Bâtiments de la copropriété', size: Sizes['2xl'] },
            <EditCopropertyBuildingsPanel coproperty={coproperty} />
          );
          return;
        case 'complements':
          openPanel(
            { title: 'Contrat en cours et prochaine AG', size: Sizes['2xl'] },
            <EditCopropertyComplementsPanel coproperty={coproperty} />
          );
          return;
        case 'claims':
          openPanel(
            { title: 'Sinistralité déclarée', size: Sizes['2xl'] },
            <EditCopropertyClaimsPanel coproperty={coproperty} choiceRequired />
          );
          return;
        default:
      }
    },
    [coproperty, riskId, quoteId, openPanel]
  );

  /**
   * Sinistralité exigée pour envoyer le contrat : le relevé d'information dans tous les cas,
   * et la situation de sinistralité renseignée quand aucun sinistre n'est déclaré.
   */
  const claimsFooter = useMemo(() => {
    const claimsHistory = coproperty?.claimsHistory;
    const situationMissing =
      !claimsHistory?.hasDeclaredClaims &&
      claimsHistory?.noClaimsLast3Years !== true &&
      claimsHistory?.notInsuredNoKnownClaims !== true;

    return (
      <>
        {claimsHistoryCompleted ? (
          <ClaimsNotice tone="success">Le relevé d'information de sinistralité a été reçu.</ClaimsNotice>
        ) : (
          <>
            {!claimsStatementUploaded && (
              <ClaimsNotice tone="warning">
                Un relevé d'information de sinistralité est obligatoire dans tous les cas pour envoyer le contrat.
              </ClaimsNotice>
            )}
            {situationMissing && (
              <ClaimsNotice tone="warning">Indiquez la situation de sinistralité pour envoyer le contrat.</ClaimsNotice>
            )}
          </>
        )}
        <ClaimsStatementUploader quoteId={quoteId} onChanged={handleClaimsStatementChanged} disabled={!canEditRisk} />
      </>
    );
  }, [coproperty, claimsHistoryCompleted, claimsStatementUploaded, quoteId, handleClaimsStatementChanged, canEditRisk]);

  if (!coproperty || !riskId) {
    return <p className="text-sm text-gray-500">Aucune copropriété rattachée à ce devis.</p>;
  }

  return (
    <CopropertySummaryPanel
      coproperty={coproperty}
      riskId={riskId}
      readOnly
      onEditSection={canEditRisk ? openSection : undefined}
      editableSections={EDITABLE_SECTIONS}
      claimsFooter={claimsFooter}
    />
  );
};
